import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";
import {
  PasswordStrength,
  isPasswordValid,
} from "../components/auth/PasswordStrength";
import { SerpixWordmark } from "../components/brand/SerpixWordmark";

export default function AcceptInvite() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [token, setToken] = useState(params.get("token") || "");
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/users/invitation/${token}`);
        setInfo(data);
        setError("");
      } catch (err) {
        setError(
          formatApiErrorDetail(err.response?.data?.detail) || "Invalid invitation"
        );
        setInfo(null);
      }
    })();
  }, [token]);

  const pwValid = isPasswordValid(password);
  const pwMatch = confirm.length > 0 && password === confirm;
  const showMismatch = confirm.length > 0 && !pwMatch;
  const canSubmit = !!info && pwValid && pwMatch && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!info) return;
    if (!pwValid) {
      setError(t("auth.pw_invalid_hint"));
      return;
    }
    if (!pwMatch) {
      setError(t("auth.pw_mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/users/accept-invitation", { token, password });
      await login(info.email, password);
      nav("/app/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
      <div className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white p-8">
        <Link to="/" className="mb-6 inline-flex items-center gap-2">
          <SerpixWordmark size="sm" />
        </Link>
        <h1 className="mb-2 font-display text-2xl font-semibold">
          {t("auth.accept_invite")}
        </h1>

        {info ? (
          <p className="mb-6 text-sm text-zinc-600">
            <span className="font-medium text-zinc-900">{info.name}</span>, you're
            invited to join{" "}
            <span className="font-medium text-zinc-900">{info.tenant_name}</span>{" "}
            as {info.role === "super_admin" ? "Super Admin" : "User"}.
          </p>
        ) : (
          <p className="mb-6 text-sm text-zinc-500">
            {t("auth.enter_invite_token")}
          </p>
        )}

        <form onSubmit={submit} className="space-y-4">
          {!info && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.invite_token")}
              </Label>
              <Input
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                data-testid={TEST_IDS.acceptInviteToken}
                className="mt-1 font-mono text-xs"
              />
            </div>
          )}
          {info && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">
                  {t("auth.email")}
                </Label>
                <Input value={info.email} disabled className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">
                  {t("auth.password")}
                </Label>
                <PasswordInput
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid={TEST_IDS.acceptInvitePassword}
                  testIdToggle="accept-invite-password-toggle"
                  className="mt-1"
                />
                <PasswordStrength password={password} hideWhenEmpty />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">
                  {t("auth.confirm_password_label")}
                </Label>
                <PasswordInput
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="accept-invite-password-confirm-input"
                  testIdToggle="accept-invite-password-confirm-toggle"
                  className="mt-1"
                  aria-invalid={showMismatch ? "true" : "false"}
                />
                {showMismatch && (
                  <p
                    className="mt-1 text-xs font-medium text-red-600"
                    data-testid="accept-invite-password-mismatch"
                  >
                    {t("auth.pw_mismatch")}
                  </p>
                )}
              </div>
            </>
          )}
          {error && <div className="text-xs text-red-600">{error}</div>}
          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid={TEST_IDS.acceptInviteSubmit}
            className="w-full bg-gradient-to-r from-[#5B4CFB] to-[#7c5cf6] text-white shadow-lg shadow-violet-500/25 hover:from-[#4f40f0] hover:to-[#6f4df0]"
          >
            {loading ? "…" : t("auth.accept_and_login")}
          </Button>
        </form>
        <Link
          to="/login"
          className="mt-6 block text-xs text-zinc-500 hover:text-zinc-900"
        >
          ← {t("auth.login")}
        </Link>
      </div>
    </div>
  );
}
